import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import './NutritionPlanStats.css';

const NutritionPlanStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    fetchNutritionPlanStats();
  }, []);

  const fetchNutritionPlanStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/nutrition-plan-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch nutrition plan statistics');
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching nutrition plan stats:', err);
      setError(err.message);
      toast.error('Failed to load nutrition plan statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getGoalTypeColor = (goalType) => {
    const colors = {
      bulk: '#10b981',
      maintain: '#6366f1',
      cut: '#f59e0b',
      custom: '#ec4899'
    };
    return colors[goalType] || '#6b7280';
  };

  const getGoalTypeIcon = (goalType) => {
    const icons = {
      bulk: 'fa-dumbbell',
      maintain: 'fa-balance-scale',
      cut: 'fa-fire',
      custom: 'fa-sliders-h'
    };
    return icons[goalType] || 'fa-bullseye';
  };

  if (loading) {
    return (
      <div className="nutrition-plan-stats loading-state">
        <div className="loading-spinner"></div>
        <p>Loading nutrition plan statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nutrition-plan-stats error-state">
        <p>{error}</p>
        <button onClick={fetchNutritionPlanStats} className="retry-button">
          <i className="fas fa-redo"></i> Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const { nutritionPlans, coachActivity, shoppingLists, clients } = stats;

  const maxGoalTypeCount = nutritionPlans.byGoalType.length > 0
    ? Math.max(...nutritionPlans.byGoalType.map(t => t.count))
    : 1;

  const maxDayCount = nutritionPlans.last30Days.length > 0
    ? Math.max(...nutritionPlans.last30Days.map(d => d.count))
    : 1;

  return (
    <div className="nutrition-plan-stats">
      <div className="stats-header">
        <h2>
          <i className="fas fa-heartbeat"></i> Nutrition Plan Statistics
        </h2>
        <button className="refresh-button" onClick={fetchNutritionPlanStats}>
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="stats-summary-grid">
        <div className="stat-card primary">
          <div className="stat-icon">
            <i className="fas fa-clipboard-list"></i>
          </div>
          <div className="stat-content">
            <h3>{nutritionPlans.total}</h3>
            <p>Total Nutrition Plans</p>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">
            <i className="fas fa-user-friends"></i>
          </div>
          <div className="stat-content">
            <h3>{coachActivity.totalClientPlans}</h3>
            <p>Coach-Created Plans</p>
          </div>
        </div>

        <div className="stat-card info">
          <div className="stat-icon">
            <i className="fas fa-shopping-cart"></i>
          </div>
          <div className="stat-content">
            <h3>{shoppingLists.fromNutritionPlans.total + shoppingLists.fromMenuItems.total}</h3>
            <p>Total Shopping Lists</p>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">
            <i className="fas fa-chalkboard-teacher"></i>
          </div>
          <div className="stat-content">
            <h3>{coachActivity.totalCoaches}</h3>
            <p>Active Coaches</p>
          </div>
        </div>

        <div className="stat-card secondary">
          <div className="stat-icon">
            <i className="fas fa-sliders-h"></i>
          </div>
          <div className="stat-content">
            <h3>{nutritionPlans.withOverrides}</h3>
            <p>Plans with Overrides</p>
          </div>
        </div>

        <div className="stat-card accent">
          <div className="stat-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-content">
            <h3>{clients.active} / {clients.total}</h3>
            <p>Active / Total Clients</p>
          </div>
        </div>
      </div>

      {/* Plans by Goal Type */}
      <div className="stats-section">
        <h3 className="section-header">
          <i className="fas fa-bullseye"></i> Plans by Goal Type
        </h3>
        <div className="goal-type-distribution">
          {nutritionPlans.byGoalType.map((type) => (
            <div key={type._id} className="goal-type-bar">
              <div className="goal-type-label">
                <span className="goal-type-icon-badge" style={{ background: getGoalTypeColor(type._id) }}>
                  <i className={`fas ${getGoalTypeIcon(type._id)}`}></i>
                </span>
                <span className="goal-type-name">{type._id || 'unknown'}</span>
              </div>
              <div className="goal-type-bar-container">
                <div
                  className="goal-type-bar-fill"
                  style={{
                    width: `${(type.count / maxGoalTypeCount) * 100}%`,
                    backgroundColor: getGoalTypeColor(type._id)
                  }}
                ></div>
              </div>
              <span className="goal-type-count">{type.count}</span>
            </div>
          ))}
          <div className="goal-type-extra-stats">
            <span className="extra-stat">
              <i className="fas fa-calendar-check"></i>
              {nutritionPlans.withWeekPlans} plans with meal schedules
            </span>
          </div>
        </div>
      </div>

      {/* Coach Leaderboard */}
      <div className="stats-section">
        <h3 className="section-header">
          <i className="fas fa-trophy"></i> Coach Leaderboard
        </h3>
        <div className="coach-leaderboard">
          {coachActivity.topCoaches.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-user-slash"></i>
              <p>No coach activity yet</p>
            </div>
          ) : (
            <table className="stats-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Coach</th>
                  <th>Business</th>
                  <th>Plans</th>
                  <th>Clients</th>
                  <th>Shopping Lists</th>
                </tr>
              </thead>
              <tbody>
                {coachActivity.topCoaches.map((coach, index) => (
                  <tr key={coach.userId}>
                    <td>
                      <span className={`rank-badge rank-${index + 1}`}>
                        {index < 3 ? ['\u{1F947}', '\u{1F948}', '\u{1F949}'][index] : `#${index + 1}`}
                      </span>
                    </td>
                    <td>
                      <div className="coach-name">{coach.userName}</div>
                      <div className="coach-email">{coach.userEmail}</div>
                    </td>
                    <td>{coach.businessName || '-'}</td>
                    <td><span className="count-badge plans">{coach.planCount}</span></td>
                    <td><span className="count-badge clients">{coach.clientCount}</span></td>
                    <td><span className="count-badge shopping">{coach.shoppingListCount}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recent Shopping Lists (Nutrition Plans) */}
      <div className="stats-section">
        <h3 className="section-header">
          <i className="fas fa-shopping-basket"></i> Recent Shopping Lists (Nutrition Plans)
          <span className="section-badge">{shoppingLists.fromNutritionPlans.total} total | {shoppingLists.fromNutritionPlans.withCoachAttribution} with coach</span>
        </h3>
        <div className="shopping-list-section">
          {shoppingLists.fromNutritionPlans.recent.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-cart-arrow-down"></i>
              <p>No nutrition plan shopping lists yet</p>
            </div>
          ) : (
            <table className="stats-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Coach</th>
                  <th>Items</th>
                  <th>Date</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {shoppingLists.fromNutritionPlans.recent.map((list) => (
                  <tr key={list._id}>
                    <td>
                      <div className="user-name">{list.userName}</div>
                      <div className="user-email">{list.userEmail}</div>
                    </td>
                    <td>
                      {list.coach ? (
                        <div className="coach-attribution">
                          <span className="coach-biz">{list.coach.businessName}</span>
                          <span className="coach-detail">{list.coach.coachName}</span>
                        </div>
                      ) : (
                        <span className="no-coach">Self</span>
                      )}
                    </td>
                    <td><span className="count-badge items">{list.itemCount}</span></td>
                    <td className="date-cell">{formatDate(list.createdAt)}</td>
                    <td>
                      {list.instacartUrl ? (
                        <a
                          href={list.instacartUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="instacart-link"
                          title="Open Instacart URL"
                        >
                          <i className="fas fa-external-link-alt"></i>
                        </a>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recent Shopping Lists (Menu Items) */}
      <div className="stats-section">
        <h3 className="section-header">
          <i className="fas fa-utensils"></i> Recent Shopping Lists (Individual Recipes)
          <span className="section-badge">{shoppingLists.fromMenuItems.total} total</span>
        </h3>
        <div className="shopping-list-section">
          {shoppingLists.fromMenuItems.recent.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-cart-arrow-down"></i>
              <p>No recipe shopping lists yet</p>
            </div>
          ) : (
            <table className="stats-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Recipe</th>
                  <th>Affiliate</th>
                  <th>Date</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {shoppingLists.fromMenuItems.recent.map((list) => (
                  <tr key={list._id}>
                    <td>
                      <div className="user-name">{list.userName}</div>
                      <div className="user-email">{list.userEmail}</div>
                    </td>
                    <td>{list.menuItemTitle}</td>
                    <td>
                      {list.affiliate ? (
                        <span className="affiliate-badge">{list.affiliate.businessName}</span>
                      ) : (
                        <span className="no-coach">Direct</span>
                      )}
                    </td>
                    <td className="date-cell">{formatDate(list.createdAt)}</td>
                    <td>
                      {list.instacartUrl ? (
                        <a
                          href={list.instacartUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="instacart-link"
                          title="Open Instacart URL"
                        >
                          <i className="fas fa-external-link-alt"></i>
                        </a>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Plans Over Time */}
      <div className="stats-section">
        <h3 className="section-header">
          <i className="fas fa-chart-line"></i> Plans Created (Last 30 Days)
        </h3>
        <div className="trend-chart">
          {nutritionPlans.last30Days.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-chart-area"></i>
              <p>No plans created in the last 30 days</p>
            </div>
          ) : (
            <div className="trend-bars">
              {nutritionPlans.last30Days.map((day) => (
                <div key={day._id} className="trend-bar-wrapper" title={`${day._id}: ${day.count} plans`}>
                  <div className="trend-bar-value">{day.count}</div>
                  <div
                    className="trend-bar"
                    style={{ height: `${(day.count / maxDayCount) * 120}px` }}
                  ></div>
                  <div className="trend-bar-date">
                    {new Date(day._id + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NutritionPlanStats;
